import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  SparklesIcon,
  CheckBadgeIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { adminManagementAPI } from '../services/api';
import { toast } from 'react-toastify';

interface CandidateSkillSummary {
  candidateInfo: {
    profileId: string;
    name: string;
    email: string;
    experience: number;
    field: string;
    location: string;
    isAvailableImmediately: boolean;
    hasResume: boolean;
    lastUpdated: string;
  };
  summary: {
    overview: string;
    keyStrengths: Array<{
      category: string;
      skills: string[];
      skillCount: number;
      relevanceScore: number;
      description: string;
    }>;
    functionalProfile: {
      totalFunctionalSkills: number;
      proficiencyLevel: string;
      categories: Record<string, {
        skills: string[];
        count: number;
        highlight: string[];
        relevance?: string;
      }>;
      recommendedRoles: string[];
      industryFocus: string;
    };
    employability?: {
      score: number;
      level: string;
      factors: Array<{
        factor: string;
        score: number;
        weight: string;
      }>;
    };
    profileCompleteness?: {
      overall: {
        percentage: number;
      };
    };
    profileAnalysis?: any;
    skillAnalytics?: any;
    marketPosition?: any;
    improvementSuggestions?: any[];
    verificationStatus?: any;
    engagementMetrics?: any;
  };
}

interface CandidateSkillSummaryProps {
  profileId: string;
  onClose: () => void;
}

// Modern Progress Ring Component
const ProgressRing: React.FC<{ 
  progress: number; 
  size?: number; 
  strokeWidth?: number; 
  color?: string;
  children?: React.ReactNode;
}> = ({ progress, size = 120, strokeWidth = 8, color = '#6366f1', children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={size}
        height={size}
        className="transform -rotate-90 transition-all duration-1000 ease-out"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-gray-200 dark:text-gray-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{
            animation: 'drawCircle 1.5s ease-out forwards'
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
};

// Modern Skill Badge Component
const SkillBadge: React.FC<{ 
  skill: string; 
  type?: 'primary' | 'secondary' | 'highlight' | 'strength';
  count?: number;
}> = ({ skill, type = 'primary', count }) => {
  const getStyles = () => {
    switch (type) {
      case 'highlight':
        return 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105';
      case 'strength':
        return 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105';
      case 'secondary':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-md hover:shadow-lg transform hover:scale-105';
    }
  };

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${getStyles()}`}>
      {skill}
      {count && count > 1 && (
        <span className="ml-1.5 bg-white bg-opacity-20 rounded-full px-1.5 py-0.5 text-xs">
          +{count - 1}
        </span>
      )}
    </span>
  );
};

// Modern Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  progress?: number;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, subtitle, icon, color, progress, trend }) => (
  <div className="group relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700">
    {/* Gradient Background */}
    <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
    
    {/* Content */}
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${color} shadow-lg`}>
          <div className="text-white">
            {icon}
          </div>
        </div>
        {trend && (
          <div className={`text-xs px-2 py-1 rounded-full ${
            trend === 'up' ? 'bg-emerald-100 text-emerald-700' :
            trend === 'down' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'}
          </div>
        )}
      </div>
      
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
        <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
        )}
      </div>

      {progress !== undefined && (
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1">
            {/* <span className="text-gray-600 dark:text-gray-400">Progress</span> */}
            <span className="font-medium">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full bg-gradient-to-r ${color} transition-all duration-1000 ease-out`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  </div>
);

const CandidateSkillSummary: React.FC<CandidateSkillSummaryProps> = ({ profileId, onClose }) => {
  const [summary, setSummary] = useState<CandidateSkillSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    loadSkillSummary();
  }, [profileId]);

  useEffect(() => {
    if (!loading && summary) {
      // Trigger staggered animations
      const timer = setTimeout(() => setAnimationStep(1), 300);
      const timer2 = setTimeout(() => setAnimationStep(2), 600);
      const timer3 = setTimeout(() => setAnimationStep(3), 900);
      
      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [loading, summary]);

  const loadSkillSummary = async () => {
    try {
      setLoading(true);
      const response = await adminManagementAPI.getCandidateProfileSummary(profileId, {
        includeAnalytics: true
      });
      
      // Fix: Handle the correct response structure
      // The backend returns: { success: true, summary: { candidateInfo: {...}, summary: {...} } }
      // We need to extract and restructure the data to match our interface
      // console.log('API Response:', response.data); // Debug log
      
      if (response.data.success && response.data.summary) {
        const apiData = response.data.summary;
        const restructuredData = {
          profileId: response.data.candidateId || profileId,
          candidateInfo: apiData.candidateInfo,
          summary: apiData.summary
        };
        setSummary(restructuredData);
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      toast.error('Failed to load candidate skill summary');
      console.error('Error loading skill summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden relative shadow-2xl">
          <div className="flex justify-center py-16">
            {/* Modern Loading Animation */}
            <div className="relative">
              <div className="w-16 h-16 border-4 border-purple-200 dark:border-purple-800 rounded-full animate-spin"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-purple-600 rounded-full animate-spin"></div>
              <div className="absolute top-2 left-2 w-12 h-12 border-4 border-transparent border-t-indigo-400 rounded-full animate-spin" 
                   style={{ animation: 'spin 1s linear infinite reverse' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-2xl w-full mx-4 p-8 shadow-2xl">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Unable to Load Summary
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We couldn't retrieve the candidate's skill summary. Please try again.
            </p>
            <button 
              onClick={onClose} 
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getEmployabilityColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'excellent': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'good': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300';
      case 'average': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
      default: return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
    }
  };

  const getProficiencyColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'expert': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-300';
      case 'advanced': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'intermediate': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300';
      case 'beginner': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden relative shadow-2xl border border-gray-200 dark:border-gray-700">
        {/* Modern Header with Gradient */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 px-8 py-6">
          <div className="flex justify-between items-start">
            <div className="text-white">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <SparklesIcon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{summary.candidateInfo.name}</h2>
                  <div className="flex items-center space-x-4 text-purple-100">
                    <span className="flex items-center">
                      <BriefcaseIcon className="w-4 h-4 mr-1" />
                      {summary.candidateInfo.field}
                    </span>
                    <span className="flex items-center">
                      <StarIcon className="w-4 h-4 mr-1" />
                      {summary.candidateInfo.experience} years
                    </span>
                    {summary.candidateInfo.isAvailableImmediately && (
                      <span className="flex items-center bg-emerald-500 bg-opacity-20 px-2 py-1 rounded-full text-emerald-100 text-xs">
                        <RocketLaunchIcon className="w-3 h-3 mr-1" />
                        Available Now
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors duration-200 p-2 hover:bg-white hover:bg-opacity-10 rounded-xl"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-8 space-y-8">
            {/* Hero Metrics with Progress Rings */}
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transform transition-all duration-700 ${
              animationStep >= 1 ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              <MetricCard
                title="Employability Score"
                value={`${summary.summary.employability?.score || 0}%`}
                subtitle={summary.summary.employability?.level || 'Unknown'}
                icon={<TrophyIcon className="w-6 h-6" />}
                color="from-emerald-500 to-teal-600"
                progress={summary.summary.employability?.score || 0}
                trend="up"
              />
              
              <MetricCard
                title="Functional Skills"
                value={summary.summary.functionalProfile.totalFunctionalSkills}
                subtitle={`${summary.summary.functionalProfile.proficiencyLevel} Level`}
                icon={<ChartBarIcon className="w-6 h-6" />}
                color="from-blue-500 to-cyan-600"
                progress={Math.min((summary.summary.functionalProfile.totalFunctionalSkills / 20) * 100, 100)}
                trend="up"
              />
              
              <MetricCard
                title="Profile Quality"
                value={`${summary.summary.profileCompleteness?.overall?.percentage || 0}%`}
                subtitle="Completeness"
                icon={<CheckBadgeIcon className="w-6 h-6" />}
                color="from-purple-500 to-indigo-600"
                progress={summary.summary.profileCompleteness?.overall?.percentage || 0}
                trend="neutral"
              />
            </div>

            {/* Professional Overview with Modern Design */}
            <div className={`transform transition-all duration-700 delay-200 ${
              animationStep >= 1 ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-indigo-100 dark:border-gray-600">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                    <AcademicCapIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Professional Overview</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{summary.summary.overview}</p>
              </div>
            </div>

            {/* Key Strengths with Enhanced Design */}
            <div className={`transform transition-all duration-700 delay-300 ${
              animationStep >= 2 ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              <div className="mb-6">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mr-3">
                    <FireIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Key Strengths</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {summary.summary.keyStrengths.slice(0, 4).map((strength, index) => (
                    <div 
                      key={index} 
                      className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-bold text-gray-900 dark:text-white text-lg">{strength.category}</h4>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{strength.relevanceScore}</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                        {strength.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-2">
                        {strength.skills.slice(0, 3).map((skill, skillIndex) => (
                          <SkillBadge key={skillIndex} skill={skill} type="strength" />
                        ))}
                        {strength.skillCount > 3 && (
                          <SkillBadge 
                            skill={`${strength.skills[3] || 'More'}`} 
                            type="highlight" 
                            count={strength.skillCount - 3} 
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Functional Skills Breakdown with Modern Cards */}
            <div className={`transform transition-all duration-700 delay-400 ${
              animationStep >= 3 ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}>
              <div className="mb-6">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mr-3">
                    <ChartBarIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Functional Skills Breakdown</h3>
                  <div className="ml-auto">
                    <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {summary.summary.functionalProfile.industryFocus} Industry
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(summary.summary.functionalProfile.categories).map(([category, data], index) => (
                    <div 
                      key={category} 
                      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700"
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                      <div className="text-center mb-4">
                        <h4 className="font-bold text-gray-900 dark:text-white text-lg capitalize mb-2">
                          {category.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                        <div className="flex justify-center">
                          <ProgressRing progress={(data.count / 10) * 100} size={80} color="#3b82f6">
                            <div className="text-center">
                              <div className="text-xl font-bold text-gray-900 dark:text-white">{data.count}</div>
                              <div className="text-xs text-gray-500">skills</div>
                            </div>
                          </ProgressRing>
                        </div>
                        <div className="mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            data.relevance === 'high' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {data.relevance} relevance
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 justify-center">
                        {data.highlight.map((skill, skillIndex) => (
                          <SkillBadge key={skillIndex} skill={skill} type="primary" />
                        ))}
                        {data.count > data.highlight.length && (
                          <SkillBadge 
                            skill="More" 
                            type="secondary" 
                            count={data.count - data.highlight.length} 
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Section with Recommendations and Roles */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recommended Roles */}
              {summary.summary.functionalProfile.recommendedRoles.length > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-green-100 dark:border-gray-600">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mr-3">
                      <LightBulbIcon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Recommended Roles</h3>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {summary.summary.functionalProfile.recommendedRoles.map((role, index) => (
                      <span 
                        key={index} 
                        className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Employability Analysis */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-purple-100 dark:border-gray-600">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mr-3">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Employability Factors</h3>
                </div>
                <div className="space-y-3">
                  {(summary.summary.employability?.factors || []).slice(0, 4).map((factor, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-xl shadow-sm">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">{factor.factor}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({factor.weight})</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-sm font-bold text-purple-600">{factor.score}%</div>
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full transition-all duration-1000 ease-out" 
                            style={{ width: `${factor.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateSkillSummary;